import { parse, type LogLine } from "@/routes/environments/log-streams/log-groups";
import { FetchError } from "@/utils/errors";

/**
 * Stream logs from a URL using Server-Sent Events.
 * Handles batching, retries, and error handling.
 */
export async function streamLogsFromUrl(
    signal: AbortSignal,
    logsUrl: string,
    logAccessToken: string,
    callback: (p: {
        clearLogs?: boolean;
        error?: Error | null;
        logLinesToAdd?: LogLine[];
        loadingMessage?: string;
    }) => void,
) {
    let batchUpdateTimer = 0;
    signal.onabort = () => {
        clearTimeout(batchUpdateTimer);
    };
    try {
        // We need to clear the logs and error before we start streaming
        callback({
            error: null,
            clearLogs: true,
        });

        const response = await fetch(logsUrl, {
            signal,
            mode: "cors",
            keepalive: false,
            headers: { Authorization: `Bearer ${logAccessToken}` },
        });

        if (!response || !response.ok || !response.body) {
            // As fetch only rejects on network errors, render status on HTTP errors
            throw new FetchError("Failed to fetch logs", response.status, response.statusText);
        }

        // Log lines are buffered and only added to the log groups once 700ms has passed without new log lines.
        // This is attempting to detect if we're trying to catch up with an existing log stream or live-tailing a new one.
        // Once it's caught up we remove the buffer latency.
        let catchUp = true;
        let batch: LogLine[] = [];
        for await (const lines of parse(response.body, signal)) {
            batch.push(...lines);
            const hasReachedMaxBufferSize = batch.length >= 10000;
            window.clearTimeout(batchUpdateTimer);
            batchUpdateTimer = window.setTimeout(
                () => {
                    const toAdd = batch;
                    batch = [];
                    catchUp = false;
                    callback({
                        logLinesToAdd: toAdd,
                        loadingMessage: "",
                    });
                },
                !catchUp || hasReachedMaxBufferSize ? 0 : 700,
            );
        }
    } catch (e) {
        // Aborted requests aren't errors, user has closed the modal (or in dev, component is mounted twice and unmounted once)
        if (e instanceof DOMException && e.name === "AbortError") {
            return;
        }

        callback({
            error: e instanceof Error ? e : null,
        });

        // re-throw for repeat attempts
        throw e;
    }
}
